import { useEffect, useRef, useState, type ReactNode } from "react";
import { loginService } from "@/api/services/loginService";
import { useUserStore } from "@/stores/react/authStore";

/**
 * 使用本地持久化 Token 向服务端恢复完整用户信息，确保权限守卫使用最新权限列表。
 */
export async function restoreUserSession(): Promise<boolean> {
    const store = useUserStore.getState();
    if (!store.token) {
        return false;
    }

    try {
        const userInfo = await loginService.getUserInfo();
        useUserStore.getState().setUserInfo(userInfo);
        return true;
    } catch {
        // Token 失效时清理本地会话，交由路由守卫回到登录页。
        useUserStore.getState().setToken("");
        return false;
    }
}

export interface AuthBootstrapProps {
    children: ReactNode;
}

/**
 * 应用启动认证门禁：完成用户信息恢复后再创建路由，避免首帧因权限为空误跳 403。
 */
export function AuthBootstrap({ children }: AuthBootstrapProps) {
    const token = useUserStore(state => state.token);
    const [ready, setReady] = useState(!token);
    const restoredToken = useRef<string | null>(null);

    useEffect(() => {
        if (!token) {
            setReady(true);
            return;
        }

        // React StrictMode 开发环境会重复执行 effect，按 Token 去重避免重复请求用户信息。
        if (restoredToken.current === token) {
            return;
        }
        restoredToken.current = token;
        setReady(false);

        void restoreUserSession().finally(() => {
            setReady(true);
        });
    }, [token]);

    if (!ready) {
        // 恢复会话期间保持页面空白，避免刷新瞬间展示临时状态文案。
        return null;
    }

    return <>{children}</>;
}
